/**
 * The search dictionary — the words a devotee can use for a thing we call something else.
 *
 * **A zero-result query is the most valuable row on this screen.** It is a devotee who wanted
 * something, typed what they call it, and was told there is nothing. Promoting one to an alias
 * is a single action so the miss and its fix cannot drift apart — the whole reason the report is
 * kept.
 *
 * **A synonym group is edited row by row, not as a set.** Each term has its own id, so adding one
 * is a POST and removing one is a DELETE. There is no replace-the-group endpoint, and inventing
 * one here would mean reading every term back and rewriting them all to change a word.
 *
 * **Deactivating is not deleting, and both are offered.** A term switched off stops matching and
 * stays readable — which is what somebody wants after a synonym turned out to pull in the wrong
 * results and they need to say why later.
 *
 * Reads admit `catalogue_content`, `growth` and `support`; every write is `catalogue_content`.
 * That split is the server's, and the buttons are gated to match so nobody is offered a door the
 * API will not open.
 */
import { useState } from 'react';
import { Chip, Note, StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { FilterBar, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { istDay } from './PricingRules';
import {
  ALIAS_ENTITIES,
  useAliases,
  useCreateAlias,
  useCreateSynonym,
  useDeleteAlias,
  useDeleteSynonym,
  usePatchAlias,
  usePatchSynonym,
  usePromoteZeroResult,
  useSynonyms,
  useZeroResults,
  useZeroResultsCsv,
  type AliasEntity,
} from '../lib/api/hooks/search';
import { usePujas } from '../lib/api/hooks/catalogue';
import { useTemples } from '../lib/api/hooks/temples';
import { useCities } from '../lib/api/hooks/geo';
import { useUrlTab } from '../lib/filterCodecs';

const TABS = [
  { key: 'synonyms', label: 'Synonyms' },
  { key: 'aliases', label: 'Aliases' },
  { key: 'zero', label: 'Found nothing' },
];

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same tab. */
const TAB_KEYS = TABS.map((t) => t.key);

/** How far back the zero-result report looks. A miss last seen in March is not today's problem. */
const WINDOWS = ['7', '30', '90'];

/** One page of each list is the whole catalogue today — 200 is the server's page ceiling. */
const EVERY = { page: 1, page_size: 200 };

/**
 * The script an alias is stored under. Exported for its own test. The normaliser never
 * transliterates, so a Devanagari alias filed as Latin would never meet a Devanagari query —
 * the server infers it the same way when it promotes a miss.
 */
export function scriptOf(text: string): 'deva' | 'latn' {
  return /[\u0900-\u097F]/.test(text) ? 'deva' : 'latn';
}

/**
 * Records named for a picker: `Griha Pravesh · #42`. The id stays in the label because two
 * temples can share a name, and the label is the value a Select hands back. Exported for its
 * own test.
 */
export function recordPicker(rows: { id: number; name: string }[]) {
  const labels = rows.map((r) => `${r.name} · #${r.id}`);
  const ids = new Map(labels.map((label, i) => [label, rows[i]!.id]));
  const names = new Map(rows.map((r) => [r.id, r.name]));
  return {
    labels,
    idOf: (label: string) => ids.get(label),
    /* A record outside the page in hand still shows its id rather than nothing. */
    nameOf: (id: number) => (names.has(id) ? `${names.get(id)} · #${id}` : `#${id}`),
  };
}

export function SearchDictionary() {
  const { scope, toast } = useConsole();
  const [tab, setTab] = useUrlTab('dictionary', TAB_KEYS, 'synonyms');
  const [group, setGroup] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [newTerm, setNewTerm] = useState('');
  const [entity, setEntity] = useState<AliasEntity>('puja');
  const [aliasFilter, setAliasFilter] = useState('');
  const [aliasRecord, setAliasRecord] = useState('');
  const [aliasText, setAliasText] = useState('');
  const [days, setDays] = useState('30');
  const [promoting, setPromoting] = useState<string | null>(null);
  const [promoteEntity, setPromoteEntity] = useState<AliasEntity>('puja');
  const [promoteRecord, setPromoteRecord] = useState('');

  /* Named, not numbered: nobody knows that Griha Pravesh is puja 42, and an alias filed against
     the wrong number is a search that silently lands on the wrong page. */
  const pujas = usePujas(EVERY);
  const temples = useTemples(EVERY);
  const cities = useCities(EVERY);
  const pickers: Record<AliasEntity, ReturnType<typeof recordPicker>> = {
    puja: recordPicker((pujas.data?.items ?? []).map((r) => ({ id: r.id, name: r.name_en }))),
    temple: recordPicker((temples.data?.items ?? []).map((r) => ({ id: r.id, name: r.name }))),
    city: recordPicker((cities.data?.items ?? []).map((r) => ({ id: r.id, name: r.name }))),
  };
  const picker = pickers[entity];
  const filterId = picker.idOf(aliasFilter);
  const aliasId = picker.idOf(aliasRecord);
  const promoteId = pickers[promoteEntity].idOf(promoteRecord);
  /* An id belongs to its entity: puja 42 is not temple 42, so a switch clears the picks. */
  const pickEntity = (next: AliasEntity) => {
    setEntity(next);
    setAliasFilter('');
    setAliasRecord('');
  };

  /* Filtered on the SERVER — a client-side filter over one page would confidently report no
     match for a group that is simply not on the page in hand. */
  const synonyms = useSynonyms(group || undefined);
  /* Narrowed on the SERVER too — "every alias for puja 42" is the question asked when a record
     looks unfindable, and filtering one page in the client would answer it from the wrong data. */
  const aliases = useAliases(entity, filterId);
  const zeroResults = useZeroResults(Number(days));
  const zeroCsv = useZeroResultsCsv();

  const createSynonym = useCreateSynonym();
  const patchSynonym = usePatchSynonym();
  const deleteSynonym = useDeleteSynonym();
  const createAlias = useCreateAlias();
  const patchAlias = usePatchAlias();
  const deleteAlias = useDeleteAlias();
  const promote = usePromoteZeroResult();

  const fail = (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) });
  const done = (message: string) => ({
    onSuccess: () => toast({ tone: 'success' as const, message }),
    onError: fail,
  });

  const synonymRows = synonyms.data?.items ?? [];
  const aliasRows = aliases.data?.items ?? [];
  const misses = zeroResults.data?.items ?? [];
  const addableSynonym = newGroup.trim() !== '' && newTerm.trim() !== '';
  const addableAlias = aliasId !== undefined && aliasText.trim() !== '';
  const promotable = promoting !== null && promoteId !== undefined;

  return (
    <>
      <FilterBar tabs={TABS} tab={tab} onTab={setTab} />

      {tab === 'synonyms' && (
        <Section
          title="Words that mean the same thing"
          note="A group is a set of interchangeable terms — every member matches every other"
        >
          <Note>
            Deactivating a term stops it matching and keeps it readable. Deleting removes it. Both
            are offered because "this pulled in the wrong results" and "this was a typo" are
            different acts.
          </Note>
          <Input
            density="admin"
            label="Filter by group"
            value={group}
            onChange={setGroup}
            placeholder="griha-pravesh"
            helper="Filtered on the server — an empty box is every group, not a group called nothing"
          />
          {synonyms.isPending && <LoadingState message="Reading the dictionary…" />}
          {synonyms.isError && (
            <ErrorState title="Could not read the synonyms" cause={errorMessage(synonyms.error)} />
          )}
          {synonyms.isSuccess && synonymRows.length === 0 && (
            <EmptyState
              icon="search"
              title={group ? 'No terms in that group' : 'No synonyms yet'}
              body="A search matches only the words the catalogue itself uses."
              inline
            />
          )}
          {synonymRows.length > 0 && (
            <DataTable
              density="compact"
              columns={[
                { key: 'group', label: 'Group', mono: true },
                { key: 'term', label: 'Term' },
                { key: 'language', label: 'Language' },
                { key: 'state', label: 'State' },
                { key: 'act', label: '' },
              ]}
              rows={synonymRows.map((row) => ({
                id: row.id,
                group: row.group_key,
                term: row.term,
                language: row.language,
                state: (
                  <StatusChip
                    tone={row.is_active ? 'success' : 'neutral'}
                    label={row.is_active ? 'active' : 'off'}
                  />
                ),
                act: (
                  <span style={{ display: 'inline-flex', gap: 'var(--sp-sm)' }}>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        patchSynonym.mutate(
                          { synonymId: row.id, body: { is_active: !row.is_active } },
                          done(row.is_active ? 'Term switched off' : 'Term switched on'),
                        )
                      }
                    >
                      {row.is_active ? 'Switch off' : 'Switch on'}
                    </ScopeAction>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        deleteSynonym.mutate({ synonymId: row.id }, done('Term deleted'))
                      }
                    >
                      Delete
                    </ScopeAction>
                  </span>
                ),
              }))}
            />
          )}
          <div
            style={{
              display: 'flex',
              gap: 'var(--sp-md)',
              alignItems: 'end',
              marginTop: 'var(--sp-md)',
            }}
          >
            <Input
              density="admin"
              label="Group"
              value={newGroup}
              onChange={setNewGroup}
              placeholder="griha-pravesh"
              style={{ flex: 1 }}
            />
            <Input
              density="admin"
              label="Term"
              value={newTerm}
              onChange={setNewTerm}
              placeholder="house warming"
              style={{ flex: 1 }}
            />
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant="secondary"
              disabled={!addableSynonym}
              onClick={() =>
                createSynonym.mutate(
                  { group_key: newGroup.trim(), term: newTerm.trim(), language: 'en' },
                  {
                    onSuccess: () => {
                      setNewTerm('');
                      toast({ tone: 'success', message: 'Term added' });
                    },
                    onError: fail,
                  },
                )
              }
            >
              Add a term
            </ScopeAction>
          </div>
        </Section>
      )}

      {tab === 'aliases' && (
        <Section
          title="Other names for one record"
          note="An alias points at a single row — a spelling, a transliteration, a local name"
        >
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
            {ALIAS_ENTITIES.map((value) => (
              <Chip
                key={value}
                variant="filter"
                label={value}
                selected={entity === value}
                onClick={() => pickEntity(value)}
              />
            ))}
          </div>
          <Select
            density="admin"
            label={`Filter by ${entity}`}
            options={picker.labels}
            value={aliasFilter}
            onChange={setAliasFilter}
            placeholder={`Every ${entity}`}
          />
          {aliases.isPending && <LoadingState message="Reading the aliases…" />}
          {aliases.isError && (
            <ErrorState title="Could not read the aliases" cause={errorMessage(aliases.error)} />
          )}
          {aliases.isSuccess && aliasRows.length === 0 && (
            <EmptyState icon="search" title={`No ${entity} aliases`} body="Only the record's own name matches." inline />
          )}
          {aliasRows.length > 0 && (
            <DataTable
              density="compact"
              columns={[
                { key: 'target', label: 'Record' },
                { key: 'alias', label: 'Alias' },
                { key: 'script', label: 'Script' },
                { key: 'source', label: 'Source' },
                { key: 'state', label: 'State' },
                { key: 'act', label: '' },
              ]}
              rows={aliasRows.map((row) => ({
                id: row.id,
                target: picker.nameOf(row.entity_id),
                alias: row.alias,
                script: row.script,
                /* Where it came from: a curator typed it, or the zero-result report promoted it.
                   Worth showing — a promoted alias is evidence somebody actually searched it. */
                source: row.source,
                state: (
                  <StatusChip
                    tone={row.is_active ? 'success' : 'neutral'}
                    label={row.is_active ? 'active' : 'off'}
                  />
                ),
                act: (
                  <span style={{ display: 'inline-flex', gap: 'var(--sp-sm)' }}>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        patchAlias.mutate(
                          { entity, aliasId: row.id, body: { is_active: !row.is_active } },
                          done(row.is_active ? 'Alias switched off' : 'Alias switched on'),
                        )
                      }
                    >
                      {row.is_active ? 'Switch off' : 'Switch on'}
                    </ScopeAction>
                    <ScopeAction
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        deleteAlias.mutate({ entity, aliasId: row.id }, done('Alias deleted'))
                      }
                    >
                      Delete
                    </ScopeAction>
                  </span>
                ),
              }))}
            />
          )}
          <div
            style={{
              display: 'flex',
              gap: 'var(--sp-md)',
              alignItems: 'end',
              marginTop: 'var(--sp-md)',
            }}
          >
            <Select
              density="admin"
              label={`Which ${entity}`}
              options={picker.labels}
              value={aliasRecord}
              onChange={setAliasRecord}
            />
            <Input
              density="admin"
              label="Alias"
              value={aliasText}
              onChange={setAliasText}
              placeholder="गृह प्रवेश"
              helper={scriptOf(aliasText) === 'deva' ? 'Filed as Devanagari' : 'Filed as Latin script'}
              style={{ flex: 1 }}
            />
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              variant="secondary"
              disabled={!addableAlias}
              onClick={() =>
                createAlias.mutate(
                  {
                    entity,
                    body: {
                      entity_id: aliasId!,
                      alias: aliasText.trim(),
                      script: scriptOf(aliasText),
                    },
                  },
                  {
                    onSuccess: () => {
                      setAliasText('');
                      toast({ tone: 'success', message: 'Alias added' });
                    },
                    onError: fail,
                  },
                )
              }
            >
              Add an alias
            </ScopeAction>
          </div>
        </Section>
      )}

      {tab === 'zero' && (
        <Section
          title="What devotees searched for and did not find"
          note="Ordered by how often — the top row is the word most people used and we did not know"
        >
          <Note>
            Promoting a query makes it an alias for a record in one action, so the miss and its fix
            cannot drift apart. That is the whole reason this report is kept.
          </Note>
          <Select
            density="admin"
            label="Looking back"
            options={WINDOWS.map((d) => `${d} days`)}
            value={`${days} days`}
            onChange={(v) => setDays(v.replace(' days', ''))}
          />
          {misses.length > 0 && (
            <ScopeAction
              scope={scope}
              need={['catalogue_content', 'growth', 'support']}
              size="sm"
              variant="secondary"
              disabled={zeroCsv.isPending}
              onClick={() =>
                zeroCsv.mutate(Number(days), {
                  onSuccess: (csv) => {
                    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `zero-results-${days}d.csv`;
                    link.click();
                    URL.revokeObjectURL(url);
                  },
                  onError: fail,
                })
              }
            >
              {zeroCsv.isPending ? 'Preparing…' : 'Download CSV'}
            </ScopeAction>
          )}
          {zeroResults.isPending && <LoadingState message="Reading the report…" />}
          {zeroResults.isError && (
            <ErrorState title="Could not read the report" cause={errorMessage(zeroResults.error)} />
          )}
          {zeroResults.isSuccess && misses.length === 0 && (
            <EmptyState
              icon="search"
              title="Nothing came back empty"
              body="Every search in this window found something."
              inline
            />
          )}
          {misses.length > 0 && (
            <DataTable
              density="compact"
              footer={<span>{zeroResults.data!.total} queries found nothing in {days} days</span>}
              columns={[
                { key: 'query', label: 'Query', mono: true },
                { key: 'count', label: 'Times', align: 'right', mono: true },
                { key: 'last', label: 'Last seen' },
                { key: 'act', label: '' },
              ]}
              rows={misses.map((row) => ({
                id: row.query_norm,
                query: row.query_norm,
                count: row.occurrences,
                last: istDay(row.last_seen_at),
                act: (
                  <ScopeAction
                    scope={scope}
                    need="catalogue_content"
                    size="sm"
                    variant="ghost"
                    onClick={() => setPromoting(row.query_norm)}
                  >
                    Promote
                  </ScopeAction>
                ),
              }))}
            />
          )}
          {promoting !== null && (
            <div
              style={{
                display: 'flex',
                gap: 'var(--sp-md)',
                alignItems: 'end',
                marginTop: 'var(--sp-md)',
              }}
            >
              <Input density="admin" label="Query" value={promoting} disabled style={{ flex: 1 }} />
              <Select
                density="admin"
                label="Points at"
                options={[...ALIAS_ENTITIES]}
                value={promoteEntity}
                onChange={(v) => {
                  setPromoteEntity(v as AliasEntity);
                  setPromoteRecord('');
                }}
              />
              <Select
                density="admin"
                label="Record"
                options={pickers[promoteEntity].labels}
                value={promoteRecord}
                onChange={setPromoteRecord}
              />
              <ScopeAction
                scope={scope}
                need="catalogue_content"
                size="sm"
                disabled={!promotable}
                onClick={() =>
                  promote.mutate(
                    {
                      query_norm: promoting,
                      entity: promoteEntity,
                      entity_id: promoteId!,
                    },
                    {
                      onSuccess: () => {
                        setPromoting(null);
                        setPromoteRecord('');
                        toast({ tone: 'success', message: 'Promoted to an alias' });
                      },
                      onError: fail,
                    },
                  )
                }
              >
                Promote to an alias
              </ScopeAction>
            </div>
          )}
        </Section>
      )}
    </>
  );
}
